import { useState } from "react";
import { T } from "../../app/tokens.js";

const colorFor = (value) => value === null ? "var(--v2-mid)"
  : value >= 8 ? "var(--v2-green)"
    : value >= 5 ? "var(--v2-amber)" : "var(--v2-red)";

export function SourceConfidence({ pillar, confidence, publicNote = "", canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [rationale, setRationale] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const current = confidence?.value ?? null;
  const color = colorFor(current);

  function beginEdit() {
    setValue(current === null ? "" : String(current));
    setRationale(confidence?.rationale ?? "");
    setError("");
    setEditing(true);
  }

  async function save(event) {
    event.preventDefault();
    const next = value === "" ? null : Number(value);
    if (value !== "" && (!/^(10|[0-9])$/.test(value) || !Number.isInteger(next))) {
      setError("Choose a whole number from 0 to 10, or clear the value.");
      return;
    }
    if (!rationale.trim()) {
      setError("Explain why, including when clearing a value.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(pillar.key, next, rationale.trim());
      setEditing(false);
    } catch (err) {
      setError(err.message || "Could not save source confidence.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid={`datatrust-confidence-${pillar.key}`} style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span
          role="meter"
          aria-label={`${pillar.label} source confidence`}
          aria-valuemin={0}
          aria-valuemax={10}
          aria-valuenow={current === null ? undefined : current}
          aria-valuetext={current === null ? "Not set" : `${current} out of 10`}
          style={{ width: 66, height: 7, borderRadius: 5, background: "var(--v2-hair)", overflow: "hidden" }}
        >
          <span style={{ display: "block", height: "100%", width: `${(current ?? 0) * 10}%`, background: color }} />
        </span>
        <strong data-testid={`datatrust-confidence-value-${pillar.key}`} style={{ color, fontSize: T.fsSm5, whiteSpace: "nowrap" }}>
          {current === null ? "Not set" : `${current}/10`}
        </strong>
        {canEdit && !editing && (
          <button type="button" onClick={beginEdit} aria-label={`Edit ${pillar.label} source confidence`}
            style={{ border: 0, background: "none", color: "var(--v2-accent)", fontSize: T.fsXxs5, fontWeight: T.fwBold, cursor: "pointer", padding: 0 }}>
            Edit
          </button>
        )}
      </div>
      <div data-testid={`datatrust-confidence-note-${pillar.key}`}
        title={publicNote || undefined}
        style={{ color: "var(--v2-mid)", fontSize: T.fsXxs5, marginTop: 4, lineHeight: 1.4,
          maxWidth: 190, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {publicNote || (pillar.excluded ? "Set when source is connected" : "No confidence note available")}
      </div>
      {editing && (
        <form onSubmit={save} style={{ marginTop: 10, padding: 12, border: "1px solid var(--v2-line)", borderRadius: 9, background: "var(--v2-tint)", minWidth: 210 }}>
          <strong style={{ display: "block", color: "var(--v2-dark)", fontSize: T.fsSm5 }}>Set source confidence - {pillar.label}</strong>
          <label style={{ display: "block", marginTop: 8, fontSize: T.fsXxs5, color: "var(--v2-mid)" }}>
            Confidence (0-10, blank to clear)
            <input aria-label={`${pillar.label} confidence value`} type="number" min="0" max="10" step="1"
              value={value} onChange={(event) => setValue(event.target.value)}
              disabled={saving} style={{ display: "block", width: "100%", padding: 6, border: "1px solid var(--v2-line)", borderRadius: 6 }} />
          </label>
          <label style={{ display: "block", marginTop: 8, fontSize: T.fsXxs5, color: "var(--v2-mid)" }}>
             Why (first sentence shown publicly; full reason audited)
            <textarea aria-label={`${pillar.label} confidence why`} value={rationale}
              onChange={(event) => setRationale(event.target.value)}
              rows={3} maxLength={2000} disabled={saving}
              style={{ display: "block", width: "100%", padding: 6, border: "1px solid var(--v2-line)", borderRadius: 6, resize: "vertical" }} />
          </label>
          {error && <div role="alert" style={{ color: "var(--v2-red)", marginTop: 6, fontSize: T.fsXxs5 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="submit" disabled={saving} style={{ background: "var(--v2-accent)", color: "white", border: 0, borderRadius: 6, padding: "5px 10px" }}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button type="button" disabled={saving} onClick={() => setEditing(false)}
              style={{ background: "var(--v2-card)", border: "1px solid var(--v2-line)", borderRadius: 6, padding: "5px 10px" }}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}